import { useState } from "react";
import { Button } from "@design-system/Button/Button";
import { Drawer } from "@design-system/Drawer";
import { Menu } from "@design-system/Menu/Menu.lib";
import { BooleanControl, PlaygroundSelect } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./OverlayPlayground.module.scss";

const drawerDirections = ["right", "left", "top", "bottom"] as const;
type DrawerDirection = (typeof drawerDirections)[number];

const drawerSizes = [
    "quarter",
    "third",
    "half",
    "fullScreen",
    "fullScreenWithNav",
] as const;
type DrawerSize = (typeof drawerSizes)[number];

export const DrawerPlayground = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [direction, setDirection] = useState<DrawerDirection>("right");
    const [size, setSize] = useState<DrawerSize>("third");
    const [isDismissible, setIsDismissible] = useState(true);
    const [hasBackdrop, setHasBackdrop] = useState(true);
    const [hasHeader, setHasHeader] = useState(true);
    const [hasPadding, setHasPadding] = useState(true);

    return (
        <SandboxLayout
            controls={
                <>
                    <section>
                        <h2>Direction</h2>
                        <PlaygroundSelect<DrawerDirection>
                            options={drawerDirections}
                            value={direction}
                            onChange={setDirection}
                        />
                        <h2 className={styles.subheading}>Size</h2>
                        <PlaygroundSelect<DrawerSize>
                            options={drawerSizes}
                            value={size}
                            onChange={setSize}
                        />
                    </section>
                    <section>
                        <h2>Features</h2>
                        <div className={styles.controls}>
                            <BooleanControl
                                label="Dismissible"
                                checked={isDismissible}
                                onChange={setIsDismissible}
                            />
                            <BooleanControl
                                label="Backdrop"
                                checked={hasBackdrop}
                                onChange={setHasBackdrop}
                            />
                            <BooleanControl
                                label="Header"
                                checked={hasHeader}
                                onChange={setHasHeader}
                            />
                            <BooleanControl
                                label="Padding"
                                checked={hasPadding}
                                onChange={setHasPadding}
                            />
                        </div>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                summary={`${direction} · ${size}`}
                output={JSON.stringify(
                    {
                        direction,
                        size,
                        dismissible: isDismissible,
                        hasBackdrop,
                        renderHeader: hasHeader,
                        noPadding: !hasPadding,
                        showcase: { isOpen },
                    },
                    null,
                    2,
                )}
            >
                <Button
                    variant="primary"
                    size="md"
                    onClick={() => setIsOpen(true)}
                >
                    Open drawer
                </Button>
                <Drawer
                    open={isOpen}
                    onOpenChange={setIsOpen}
                    direction={direction}
                    size={size}
                    dismissible={isDismissible}
                    hasBackdrop={hasBackdrop}
                    renderHeader={hasHeader}
                    noPadding={!hasPadding}
                    headerAddonLeft={<strong>Transaction details</strong>}
                >
                    <div className={styles.overlayBody}>
                        <h3>Stripe subscription</h3>
                        <p>
                            A nested menu verifies that floating content is
                            mounted inside the drawer overlay.
                        </p>
                        <Menu.Root
                            trigger={
                                <Button variant="primary" size="md">
                                    Open nested menu
                                </Button>
                            }
                        >
                            <Menu.Item>Mark as reviewed</Menu.Item>
                            <Menu.Item>Add note</Menu.Item>
                        </Menu.Root>
                    </div>
                </Drawer>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
